import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './AttendancePage.css';
import { submitAttendance, getProjectLocations, getTodayAttendance } from '../services/googleScriptServiceCORS';
import { reverseGeocode } from '../services/geocodingService';
import { getSuccessMessage } from '../utils/greetings';
import Icon from './Icon';
import LoadingSpinner from './LoadingSpinner';
import { getCached, setCached, CACHE_TTL } from '../utils/cache';
import CameraPreview from './CameraPreview';

function AttendancePage({ employee, apiBaseUrl }) {
  // Helper function to disable debug endpoints in production
  const debugLog = (logData) => {
    if (process.env.NODE_ENV === 'development') {
      fetch('http://127.0.0.1:7244/ingest/ea6218cf-eb2d-4eb6-a10c-66d9cf5c51b1', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(logData)
      }).catch(() => {}); // Silently fail
    }
  };

  // Helper function to log debug messages only in development
  const debugConsoleLog = (...args) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(...args);
    }
  };

  const [todayAttendance, setTodayAttendance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [locationPermission, setLocationPermission] = useState('prompt'); // 'prompt', 'granted', 'denied'
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const [isUserRequesting, setIsUserRequesting] = useState(false); // Flag để biết user đang click button
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [explanation, setExplanation] = useState('');
  const [requiresExplanation, setRequiresExplanation] = useState(false);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [projectLocations, setProjectLocations] = useState([]);
  const [applyBeauty, setApplyBeauty] = useState(true); // Mặc định bật chế độ làm đẹp
  const [beautyIntensity, setBeautyIntensity] = useState(50); // Cường độ làm đẹp (0-100), mặc định 50%
  const [gpsProgress, setGpsProgress] = useState(0); // Progress indicator cho GPS
  const [showCameraPreview, setShowCameraPreview] = useState(false); // Hiển thị camera preview

  // Lazy load: chỉ load khi component mount, không load ngay

  // #region agent log - Monitor state changes
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const logData = {location:'AttendancePage.js:28',message:'state changed',data:{locationPermission,isRequestingLocation,isUserRequesting,hasLocation:!!location,locationError},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A,E'};
      if (process.env.NODE_ENV === 'development') {
        console.log('[DEBUG] State changed:', logData);
      }
      debugLog(logData);
    }
  }, [locationPermission, isRequestingLocation, isUserRequesting, location, locationError]);
  // #endregion

  const loadProjectLocations = useCallback(async () => {
    try {
      const locations = await getProjectLocations();
      setProjectLocations(locations || []);
    } catch (error) {
      console.error('Error loading project locations:', error);
      // Set empty array nếu có lỗi
      setProjectLocations([]);
    }
  }, []);

  // Reset validation warnings khi location thay đổi
  useEffect(() => {
    if (location) {
      setValidationWarnings([]);
      setRequiresExplanation(false);
      setExplanation('');
    }
  }, [location]);

  const loadTodayAttendance = useCallback(async () => {
    try {
      // Sử dụng Google Script thay vì FastAPI
      const useGoogleScript = process.env.REACT_APP_GOOGLE_SCRIPT_URL;
      
      if (useGoogleScript) {
        const todayData = await getTodayAttendance(employee.code);
        setTodayAttendance(todayData);
        return todayData; // Return để có thể check trong retry logic
      } else {
        // Fallback to FastAPI nếu không có Google Script URL
        try {
          const response = await axios.get(`${apiBaseUrl}/api/attendance/today/${employee.id}`);
          setTodayAttendance(response.data);
        } catch (error) {
          if (error.response?.status !== 404) {
            console.error('Error loading today attendance:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error loading today attendance:', error);
      setTodayAttendance(null);
    }
  }, [employee, apiBaseUrl]);

  // Lazy load: chỉ load khi component mount, không load ngay
  useEffect(() => {
    loadTodayAttendance();
  }, [loadTodayAttendance]);

  const checkLocationPermission = useCallback(() => {
    // #region agent log
    if (process.env.NODE_ENV === 'development') {
      const logData = {location:'AttendancePage.js:75',message:'checkLocationPermission called',data:{hasGeolocation:!!navigator.geolocation,hasPermissions:!!(navigator.permissions&&navigator.permissions.query),currentPermission:locationPermission,isUserRequesting},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'};
      if (process.env.NODE_ENV === 'development') {
        console.log('[DEBUG]', logData);
        debugLog(logData);
      }
    }
    // #endregion
    
    // Không override state nếu user đang click button
    if (isUserRequesting) {
      // #region agent log
      const logDataSkip = {location:'AttendancePage.js:80',message:'checkLocationPermission skipped (user requesting)',data:{},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'};
      if (process.env.NODE_ENV === 'development') {
        console.log('[DEBUG]', logDataSkip);
        debugLog(logDataSkip);
      }
      // #endregion
      return;
    }
    
    if (!navigator.geolocation) {
      setLocationError('Trình duyệt không hỗ trợ định vị');
      setLocationPermission('denied');
      return;
    }

    // Kiểm tra permission status (nếu browser hỗ trợ)
    // Lưu ý: iOS Safari không hỗ trợ navigator.permissions API
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        // #region agent log
        const logDataPerm = {location:'AttendancePage.js:87',message:'permissions.query result',data:{state:result.state,isUserRequesting},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'};
        if (process.env.NODE_ENV === 'development') {
          console.log('[DEBUG]', logDataPerm);
          debugLog(logDataPerm);
        }
        // #endregion
        // CRITICAL: Không update state nếu user đang request để tránh đóng popup
        // Check isUserRequesting trong closure để đảm bảo không bị stale
        if (!isUserRequesting) {
          setLocationPermission(result.state);
          if (result.state === 'granted') {
            getCurrentLocation();
          }
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.log('[DEBUG] Skipping permission update - user is requesting');
          }
        }
      }).catch((err) => {
        // #region agent log
        const logDataErr = {location:'AttendancePage.js:92',message:'permissions.query error',data:{error:err.message},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'};
        if (process.env.NODE_ENV === 'development') {
          console.log('[DEBUG]', logDataErr);
          debugLog(logDataErr);
        }
        // #endregion
        // Nếu permissions API không hoạt động (iOS Safari), đặt là 'prompt'
        if (!isUserRequesting) {
          setLocationPermission('prompt');
        }
      });
    } else {
      // #region agent log
      const logDataNoPerm = {location:'AttendancePage.js:97',message:'permissions API not available, setting prompt',data:{},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'};
      if (process.env.NODE_ENV === 'development') {
        console.log('[DEBUG]', logDataNoPerm);
        debugLog(logDataNoPerm);
      }
      // #endregion
      // iOS Safari và một số mobile browsers không hỗ trợ permissions API
      // Đặt là 'prompt' để user có thể click nút xin quyền
      if (!isUserRequesting) {
        setLocationPermission('prompt');
      }
    }
  }, [locationPermission, isUserRequesting]);

  // useEffect để check location permission và load project locations
  useEffect(() => {
    // Check HTTPS protocol - Geolocation API requires HTTPS (or localhost)
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    const isSecure = protocol === 'https:' || hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
    
    // #region agent log (only in development)
    if (process.env.NODE_ENV === 'development') {
    const logDataProtocol = {location:'AttendancePage.js:22',message:'protocol check',data:{protocol,hostname,isSecure},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'};
    console.log('[DEBUG] Protocol check:', logDataProtocol);
      debugLog(logDataProtocol);
    }
    // #endregion
    
    if (!isSecure) {
      console.warn('[WARNING] Geolocation API requires HTTPS or localhost. Current protocol:', protocol);
      setLocationError('⚠️ Geolocation API yêu cầu HTTPS hoặc localhost. Vui lòng truy cập qua HTTPS hoặc localhost để sử dụng tính năng này.');
      setLocationPermission('denied');
      return;
    }
    
    // Không tự động yêu cầu vị trí, đợi user click nút
    // Only check permission if user is not requesting
    if (!isUserRequesting) {
      checkLocationPermission();
    }
    // Load danh sách địa điểm dự án
    loadProjectLocations();
  }, [employee, isUserRequesting, checkLocationPermission, loadProjectLocations]);

  // Handler function for requesting location - can be called from both onClick and onTouchEnd
  const handleRequestLocation = () => {
    debugConsoleLog('[DEBUG] handleRequestLocation called', {isRequestingLocation, locationPermission, isUserRequesting});
    
    // Prevent multiple calls
    if (isRequestingLocation || isUserRequesting) {
      debugConsoleLog('[DEBUG] Already requesting, skipping');
      return;
    }
    
    if (!navigator.geolocation) {
      debugConsoleLog('[DEBUG] Geolocation not available');
      setLocationError('Trình duyệt không hỗ trợ định vị');
      setLocationPermission('denied');
      return;
    }
    
    // CRITICAL: Set flags FIRST using functional updates to ensure they're set immediately
    // This prevents checkLocationPermission from interfering
    debugConsoleLog('[DEBUG] Setting flags FIRST with functional updates');
    setIsUserRequesting(prev => {
      debugConsoleLog('[DEBUG] setIsUserRequesting', prev, '->', true);
      return true;
    });
    setIsRequestingLocation(prev => {
      debugConsoleLog('[DEBUG] setIsRequestingLocation', prev, '->', true);
      return true;
    });
    setLocationError(null);
    // CRITICAL: DON'T change locationPermission here - keep current state
    // Changing state will trigger re-render and close the popup
    // Only update after user grants/denies permission in callbacks
    
    // CRITICAL: Call getCurrentPosition DIRECTLY from user gesture handler
    // This MUST be called synchronously, not in setTimeout
    // This is required for mobile browsers (especially iOS Safari)
    debugConsoleLog('[DEBUG] Calling getCurrentPosition IMMEDIATELY - popup should appear now');
    
    // Store watch ID to clear it later
    let watchId = null;
    
    try {
      // Try getCurrentPosition first
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          debugConsoleLog('[DEBUG] getCurrentPosition SUCCESS - user granted permission', position.coords);
          // Clear watch if it was set
          if (watchId !== null) {
            navigator.geolocation.clearWatch(watchId);
          }
          const { latitude, longitude } = position.coords;
          // Update state AFTER permission is granted
          setLocationPermission('granted');
          setIsRequestingLocation(false);
          setIsUserRequesting(false);
          
          // TỐI ƯU: Set location ngay lập tức, reverse geocode async không block
            setLocation({ latitude, longitude, address: '', accuracy: position.coords.accuracy || null });
          // Reverse geocode trong background (không block UI)
          reverseGeocode(latitude, longitude).then(address => {
            setLocation(prev => prev ? { ...prev, address } : null);
          }).catch(() => {
            // Ignore geocoding errors, coordinates đã đủ
          });
        },
        (error) => {
          debugConsoleLog('[DEBUG] getCurrentPosition ERROR - user denied or error occurred', error.code, error.message, error);
          
          // If permission denied, try watchPosition as alternative
          // Sometimes watchPosition can trigger popup when getCurrentPosition doesn't
          if (error.code === error.PERMISSION_DENIED) {
            debugConsoleLog('[DEBUG] Permission denied, trying watchPosition as alternative');
            try {
              watchId = navigator.geolocation.watchPosition(
                async (position) => {
                  debugConsoleLog('[DEBUG] watchPosition SUCCESS - user granted permission', position.coords);
                  // Clear watch after getting position
                  if (watchId !== null) {
                    navigator.geolocation.clearWatch(watchId);
                    watchId = null;
                  }
                  const { latitude, longitude } = position.coords;
                  setLocationPermission('granted');
                  setIsRequestingLocation(false);
                  setIsUserRequesting(false);
                  
                  // TỐI ƯU: Set location ngay lập tức, reverse geocode async không block
                    setLocation({ latitude, longitude, address: '', accuracy: position.coords.accuracy || null });
                  // Reverse geocode trong background (không block UI)
                  reverseGeocode(latitude, longitude).then(address => {
                    setLocation(prev => prev ? { ...prev, address } : null);
                  }).catch(() => {
                    // Ignore geocoding errors, coordinates đã đủ
                  });
                },
                (watchError) => {
                  debugConsoleLog('[DEBUG] watchPosition ERROR', watchError.code, watchError.message);
                  // Clear watch on error
                  if (watchId !== null) {
                    navigator.geolocation.clearWatch(watchId);
                    watchId = null;
                  }
                  // Fall through to error handling
                  handleGeolocationError(watchError);
                },
                { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
              );
              // Clear watch after 5 seconds if no response
              setTimeout(() => {
                if (watchId !== null) {
                  debugConsoleLog('[DEBUG] Clearing watchPosition after timeout');
                  navigator.geolocation.clearWatch(watchId);
                  watchId = null;
                }
              }, 5000);
              return; // Don't call handleGeolocationError yet
            } catch (watchErr) {
              console.error('[DEBUG] Exception calling watchPosition', watchErr);
            }
          }
          
          // Handle error
          handleGeolocationError(error);
        },
        { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
      );
    } catch (err) {
      console.error('[DEBUG] Exception calling getCurrentPosition', err);
      setIsRequestingLocation(false);
      setIsUserRequesting(false);
      setLocationError('Lỗi khi gọi geolocation API: ' + err.message);
    }
    
    // Helper function to handle geolocation errors
    function handleGeolocationError(error) {
      setIsRequestingLocation(false);
      setIsUserRequesting(false);
      let errorMessage = 'Không thể lấy vị trí';
      switch(error.code) {
        case error.PERMISSION_DENIED:
          errorMessage = 'Bạn đã từ chối quyền truy cập vị trí. Vui lòng cấp quyền trong cài đặt trình duyệt hoặc cài đặt của thiết bị.';
          setLocationPermission('denied');
          break;
        case error.POSITION_UNAVAILABLE:
          errorMessage = 'Không thể xác định vị trí. Vui lòng kiểm tra GPS và kết nối mạng, sau đó thử lại.';
          setLocationPermission('denied');
          break;
        case error.TIMEOUT:
          errorMessage = 'Hết thời gian chờ lấy vị trí. Vui lòng đảm bảo GPS đã bật và thử lại.';
          setLocationPermission('denied');
          break;
        default:
          errorMessage = 'Lỗi khi lấy vị trí. Vui lòng thử lại.';
          setLocationPermission('denied');
          break;
      }
      setLocationError(errorMessage);
    }
  };

  const requestLocationPermission = () => {
    // #region agent log
    if (process.env.NODE_ENV === 'development') {
      const logData1 = {location:'AttendancePage.js:101',message:'requestLocationPermission called',data:{userAgent:navigator.userAgent,hasGeolocation:!!navigator.geolocation,hasPermissions:!!(navigator.permissions&&navigator.permissions.query)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A,B,C,D'};
      console.log('[DEBUG]', logData1);
      debugLog(logData1);
    }
    // #endregion
    
    // Đảm bảo function được gọi
    if (!navigator.geolocation) {
      // #region agent log
      if (process.env.NODE_ENV === 'development') {
        const logData2 = {location:'AttendancePage.js:105',message:'geolocation not available',data:{userAgent:navigator.userAgent},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'};
        console.log('[DEBUG]', logData2);
        debugLog(logData2);
      }
      // #endregion
      setLocationError('Trình duyệt không hỗ trợ định vị');
      setLocationPermission('denied');
      return;
    }

    // #region agent log
    if (process.env.NODE_ENV === 'development') {
      const logData3 = {location:'AttendancePage.js:110',message:'before state updates',data:{currentPermission:locationPermission,currentRequesting:isRequestingLocation},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A,E'};
      console.log('[DEBUG]', logData3);
      debugLog(logData3);
    }
    // #endregion

    // Set flag để prevent checkLocationPermission từ override
    setIsUserRequesting(true);
    
    // Update state ngay lập tức để UI phản hồi - sử dụng functional updates để tránh stale closures
    setIsRequestingLocation(prev => {
      // #region agent log
      if (process.env.NODE_ENV === 'development') {
        const logData4 = {location:'AttendancePage.js:115',message:'setIsRequestingLocation called',data:{prev,new:true},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'};
        console.log('[DEBUG]', logData4);
        debugLog(logData4);
      }
      // #endregion
      return true;
    });
    setLocationError(null);
    setLocationPermission(prev => {
      // #region agent log
      if (process.env.NODE_ENV === 'development') {
        const logData5 = {location:'AttendancePage.js:120',message:'setLocationPermission called',data:{prev,new:'prompt'},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'};
        console.log('[DEBUG]', logData5);
        debugLog(logData5);
      }
      // #endregion
      return 'prompt';
    });
    
    // #region agent log
    if (process.env.NODE_ENV === 'development') {
      const logData6 = {location:'AttendancePage.js:125',message:'after state updates',data:{setIsRequestingLocation:true,setLocationPermission:'prompt'},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'};
      console.log('[DEBUG]', logData6);
      debugLog(logData6);
    }
    // #endregion
    
    // Options cho geolocation - tối ưu cho mobile
    // QUAN TRỌNG: Phải gọi getCurrentPosition TRỰC TIẾP trong user gesture handler
    // Không được delay hoặc wrap trong setTimeout vì mobile browsers sẽ reject
    // Check cache first
    const cachedLocation = getCached('lastLocation', CACHE_TTL.LOCATION);
    if (cachedLocation && cachedLocation.latitude && cachedLocation.longitude) {
      console.log('[Cache] Using cached location');
      setLocation(cachedLocation);
      setLocationPermission('granted');
      setIsRequestingLocation(false);
      setIsUserRequesting(false);
      setGpsProgress(0);
      // Still try to get fresh location in background
      // But don't block UI
    }
    
    // Start progress indicator
    setGpsProgress(10);
    const progressInterval = setInterval(() => {
      setGpsProgress(prev => {
        if (prev >= 90) return prev; // Don't go to 100 until we get position
        return prev + 5;
      });
    }, 500);
    
    const options = {
      enableHighAccuracy: true, // Sử dụng GPS nếu có
      timeout: 15000, // Giảm từ 20s xuống 15s
      maximumAge: 0 // Không cache, luôn lấy vị trí mới
    };
    
    // #region agent log
    if (process.env.NODE_ENV === 'development') {
      const logData7 = {location:'AttendancePage.js:209',message:'calling getCurrentPosition IMMEDIATELY',data:{options:JSON.stringify(options),userAgent:navigator.userAgent},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'};
      console.log('[DEBUG]', logData7);
      debugLog(logData7);
    }
    // #endregion
    
    // GỌI TRỰC TIẾP - không delay, không async wrapper
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        // #region agent log
        if (process.env.NODE_ENV === 'development') {
          const logData8 = {location:'AttendancePage.js:127',message:'getCurrentPosition success',data:{latitude:position.coords.latitude,longitude:position.coords.longitude,accuracy:position.coords.accuracy},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'};
          console.log('[DEBUG]', logData8);
          debugLog(logData8);
        }
        // #endregion
        clearInterval(progressInterval);
        setGpsProgress(100);
        
        const { latitude, longitude } = position.coords;
        const locationData = { 
          latitude, 
          longitude, 
          address: '',
          accuracy: position.coords.accuracy || null
        };
        
        // Cache location
        setCached('lastLocation', locationData);
        
        setLocationPermission('granted');
        setIsRequestingLocation(false);
        setIsUserRequesting(false); // Clear flag after success
        
        // TỐI ƯU: Set location ngay lập tức, reverse geocode async không block
        setLocation(locationData);
        
        // Reset progress after a moment
        setTimeout(() => setGpsProgress(0), 500);
        
        // Reverse geocode trong background (không block UI)
        reverseGeocode(latitude, longitude).then(address => {
          const updatedLocation = { ...locationData, address };
          setLocation(updatedLocation);
          setCached('lastLocation', updatedLocation);
        }).catch(() => {
          // Ignore geocoding errors, coordinates đã đủ
        });
      },
      (error) => {
        clearInterval(progressInterval);
        setGpsProgress(0);
        
        // #region agent log
        if (process.env.NODE_ENV === 'development') {
          const logData9 = {location:'AttendancePage.js:147',message:'getCurrentPosition error',data:{code:error.code,message:error.message,PERMISSION_DENIED:error.PERMISSION_DENIED,POSITION_UNAVAILABLE:error.POSITION_UNAVAILABLE,TIMEOUT:error.TIMEOUT},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B,C'};
          console.log('[DEBUG]', logData9);
          debugLog(logData9);
        }
        // #endregion
        setIsRequestingLocation(false);
        let errorMessage = 'Không thể lấy vị trí';
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Bạn đã từ chối quyền truy cập vị trí. Vui lòng cấp quyền trong cài đặt trình duyệt hoặc cài đặt của thiết bị.';
            setLocationPermission('denied');
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Không thể xác định vị trí. Vui lòng kiểm tra GPS và kết nối mạng, sau đó thử lại.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Hết thời gian chờ lấy vị trí. Vui lòng đảm bảo GPS đã bật và thử lại.';
            break;
          default:
            errorMessage = 'Lỗi khi lấy vị trí. Vui lòng thử lại.';
            break;
        }
        
        setLocationError(errorMessage);
        setIsUserRequesting(false); // Clear flag after error
      },
      options
    );
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Trình duyệt không hỗ trợ định vị');
      return;
    }

    setLocationError(null);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        // TỐI ƯU: Set location ngay lập tức, reverse geocode async không block
          setLocation({ 
            latitude, 
            longitude, 
            address: '',
            accuracy: position.coords.accuracy || null
          });
        // Reverse geocode trong background (không block UI)
        reverseGeocode(latitude, longitude).then(address => {
          setLocation(prev => prev ? { ...prev, address } : null);
        }).catch(() => {
          // Ignore geocoding errors, coordinates đã đủ
        });
      },
      (error) => {
        let errorMessage = 'Không thể lấy vị trí';
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Bạn đã từ chối quyền truy cập vị trí.';
            setLocationPermission('denied');
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Thông tin vị trí không khả dụng.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Hết thời gian chờ lấy vị trí.';
            break;
          default:
            errorMessage = 'Lỗi: ' + error.message;
            break;
        }
        
        setLocationError(errorMessage);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    );
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      await processImageFile(file);
    }
  };

  const processImageFile = async (file) => {
    try {
      // TỐI ƯU: Compress ảnh trước khi lưu để giảm dung lượng, tích hợp chế độ làm đẹp
      const { compressImageForAttendance } = await import('../utils/imageCompression');
      const compressedFile = await compressImageForAttendance(file, null, applyBeauty, beautyIntensity);
      
      setImage(compressedFile);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(compressedFile);
    } catch (error) {
      console.error('Error compressing image:', error);
      // Fallback: sử dụng file gốc nếu compress thất bại
      setImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCameraCapture = async (file) => {
    await processImageFile(file);
    setShowCameraPreview(false);
  };

  // Kiểm tra thời gian và vị trí để xác định có cần giải trình không
  // Chỉ yêu cầu giải trình khi: checkin sau 8h30 hoặc checkout trước 16h30
  const checkValidation = (type) => {
    const warnings = [];
    let needExplanation = false;

    // Kiểm tra thời gian
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const currentTime = hours * 60 + minutes; // Tổng phút trong ngày

    if (type === 'checkin') {
      // Giờ vào hợp lệ: trước 8h30 (8*60 + 30 = 510 phút)
      const validCheckinTime = 8 * 60 + 30; // 8h30
      if (currentTime > validCheckinTime) {
        warnings.push('Giờ vào sau 8h30');
        needExplanation = true; // Chỉ yêu cầu giải trình khi checkin sau 8h30
      }
    } else if (type === 'checkout') {
      // Giờ ra hợp lệ: sau 16h30 (16*60 + 30 = 990 phút)
      const validCheckoutTime = 16 * 60 + 30; // 16h30
      if (currentTime < validCheckoutTime) {
        warnings.push('Giờ ra trước 16h30');
        needExplanation = true; // Chỉ yêu cầu giải trình khi checkout trước 16h30
      }
    }

    // Kiểm tra vị trí (chỉ cho checkin, checkout không cần validation vị trí)
    // Với checkout: không kiểm tra vị trí, chỉ kiểm tra thời gian
    if (type === 'checkin' && location && projectLocations.length > 0) {
      // Kiểm tra xem vị trí có nằm trong bất kỳ địa điểm dự án nào không
      let isInAnyLocation = false;
      let minDistance = Infinity;
      let nearestLocation = null;

      for (const project of projectLocations) {
        if (project.latitude && project.longitude && project.radius > 0) {
          const distance = calculateDistance(
            location.latitude,
            location.longitude,
            project.latitude,
            project.longitude
          );

          if (distance <= project.radius) {
            isInAnyLocation = true;
            break;
          }

          // Lưu khoảng cách gần nhất để hiển thị thông tin
          if (distance < minDistance) {
            minDistance = distance;
            nearestLocation = project;
          }
        }
      }

      // Nếu không nằm trong bất kỳ địa điểm nào
      if (!isInAnyLocation) {
        const distanceText = minDistance < Infinity 
          ? `cách ${Math.round(minDistance)}m từ "${nearestLocation?.name || 'khu vực gần nhất'}"`
          : 'không xác định được khoảng cách';
        warnings.push(`Ngoài khu vực làm việc (${distanceText})`);
        needExplanation = true;
      }
    } else if (location && projectLocations.length === 0) {
      // Nếu không có địa điểm dự án nào được cấu hình, không yêu cầu giải trình về vị trí
      // (có thể chưa cấu hình hoặc đang tải)
    }

    setValidationWarnings(warnings);
    setRequiresExplanation(needExplanation);
    return { warnings, needExplanation };
  };

  // Tính khoảng cách giữa 2 điểm GPS (Haversine formula)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000; // Bán kính Trái Đất (mét)
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Khoảng cách (mét)
  };

  const handleCheckin = async () => {
    if (!location) {
      alert('Vui lòng lấy vị trí trước khi chấm công');
      return;
    }

    // Kiểm tra validation client-side
    const { needExplanation } = checkValidation('checkin');
    if (needExplanation && !explanation.trim()) {
      alert('Vui lòng nhập giải trình vì:\n' + validationWarnings.join('\n'));
      return;
    }

    // Kiểm tra nếu backend đã yêu cầu giải trình
    if (requiresExplanation && !explanation.trim()) {
      alert('Chấm công không hợp lệ. Vui lòng nhập giải trình để tiếp tục.');
      // Scroll đến phần giải trình
      setTimeout(() => {
        const explanationSection = document.querySelector('.explanation-section');
        if (explanationSection) {
          explanationSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }

    setLoading(true);
    try {
      // Kiểm tra xem có dùng Google Script không
      const useGoogleScript = process.env.REACT_APP_GOOGLE_SCRIPT_URL;
      
      if (useGoogleScript) {
        // Gửi đến Google Apps Script
        const result = await submitAttendance({
          employee_id: employee.id,
          employee_code: employee.code,
          employee_name: employee.name,
          type: 'checkin',
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy: location.accuracy || null,
          address: location.address,
          image: image,
          explanation: explanation.trim()
        });

        if (result.ok) {
          alert(getSuccessMessage('checkin'));
          setImage(null);
          setImagePreview(null);
          setExplanation('');
          setRequiresExplanation(false);
          setValidationWarnings([]);
          
          // Tạo todayAttendance object từ response ngay lập tức
          const newTodayAttendance = {
            checkin_time: result.timestamp || new Date().toISOString(),
            checkout_time: null,
            work_hours: 0
          };
          
          console.log('Setting todayAttendance from checkin response:', newTodayAttendance);
          setTodayAttendance(newTodayAttendance);
          
          // Bỏ background refresh để tăng tốc độ - đã có data từ response
        } else {
          // Kiểm tra xem có yêu cầu giải trình không
          if (result.requiresExplanation) {
            setRequiresExplanation(true);
            const errorMsg = result.error || 'Chấm công không hợp lệ. Vui lòng nhập giải trình để tiếp tục.';
            const noteMsg = result.note || '';
            setValidationWarnings([errorMsg, noteMsg].filter(Boolean));
            // Scroll đến phần giải trình
            setTimeout(() => {
              const explanationSection = document.querySelector('.explanation-section');
              if (explanationSection) {
                explanationSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            }, 100);
            return; // Không throw error, để user nhập giải trình
          }
          throw new Error(result.error || 'Có lỗi xảy ra');
        }
      } else {
        // Gửi đến FastAPI backend (cách cũ)
        const formData = new FormData();
        formData.append('employee_id', employee.id);
        formData.append('latitude', location.latitude);
        formData.append('longitude', location.longitude);
        formData.append('address', location.address);
        if (image) {
          formData.append('image', image);
        }

        await axios.post(`${apiBaseUrl}/api/attendance/checkin`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        const now = new Date();
        const dateStr = now.toLocaleDateString('vi-VN');
        const timeStr = now.toLocaleTimeString('vi-VN');
        const successMessage = `Xin chào ${employee.name}, chúc mừng bạn đã chấm công thành công ${dateStr} ${timeStr}`;
        alert(successMessage);
        setImage(null);
        setImagePreview(null);
        loadTodayAttendance();
      }
    } catch (error) {
      console.error('Checkin error:', error);
      alert(error.message || error.response?.data?.detail || 'Có lỗi xảy ra khi chấm công vào');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (!location) {
      alert('Vui lòng lấy vị trí trước khi chấm công');
      return;
    }

    if (!image) {
      alert('Vui lòng chụp ảnh trước khi chấm công ra');
      return;
    }

    // Kiểm tra nếu backend đã yêu cầu giải trình
    if (requiresExplanation && !explanation.trim()) {
      alert('Chấm công không hợp lệ. Vui lòng nhập giải trình để tiếp tục.');
      // Scroll đến phần giải trình
      setTimeout(() => {
        const explanationSection = document.querySelector('.explanation-section');
        if (explanationSection) {
          explanationSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }

    setLoading(true);
    try {
      // Starting checkout process

      // Kiểm tra xem có dùng Google Script không
      const useGoogleScript = process.env.REACT_APP_GOOGLE_SCRIPT_URL;
      
      if (useGoogleScript) {
        // Gửi đến Google Apps Script
        const result = await submitAttendance({
          employee_id: employee.id,
          employee_code: employee.code,
          employee_name: employee.name,
          type: 'checkout',
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy: location.accuracy || null,
          address: location.address,
          image: image,
          explanation: explanation.trim()
        });


        if (result.ok) {
          alert(getSuccessMessage('checkout'));
          setImage(null);
          setImagePreview(null);
          setExplanation('');
          setRequiresExplanation(false);
          setValidationWarnings([]);
          
          // Cập nhật todayAttendance với checkout_time từ response
          setTodayAttendance(prev => {
            if (prev && prev.checkin_time) {
              const checkoutTime = result.timestamp || new Date().toISOString();
              const checkinTime = new Date(prev.checkin_time);
              const checkout = new Date(checkoutTime);
              const workHours = (checkout - checkinTime) / (1000 * 60 * 60);
              
              return {
                ...prev,
                checkout_time: checkoutTime,
                work_hours: workHours > 0 ? workHours : 0
              };
            }
            return prev;
          });
          
          // Bỏ background refresh để tăng tốc độ - đã có data từ response
        } else {
          // Kiểm tra xem có yêu cầu giải trình không
          if (result.requiresExplanation) {
            setRequiresExplanation(true);
            const errorMsg = result.error || 'Chấm công không hợp lệ. Vui lòng nhập giải trình để tiếp tục.';
            const noteMsg = result.note || '';
            setValidationWarnings([errorMsg, noteMsg].filter(Boolean));
            // Scroll đến phần giải trình
            setTimeout(() => {
              const explanationSection = document.querySelector('.explanation-section');
              if (explanationSection) {
                explanationSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            }, 100);
            return; // Không throw error, để user nhập giải trình
          }
          throw new Error(result.error || 'Có lỗi xảy ra');
        }
      } else {
        // Gửi đến FastAPI backend (cách cũ)
        const formData = new FormData();
        formData.append('employee_id', employee.id);
        formData.append('latitude', location.latitude);
        formData.append('longitude', location.longitude);
        formData.append('address', location.address);
        if (image) {
          formData.append('image', image);
        }

        await axios.post(`${apiBaseUrl}/api/attendance/checkout`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        const now = new Date();
        const dateStr = now.toLocaleDateString('vi-VN');
        const timeStr = now.toLocaleTimeString('vi-VN');
        const successMessage = `Xin chào ${employee.name}, chúc mừng bạn đã chấm công thành công ${dateStr} ${timeStr}`;
        alert(successMessage);
        setImage(null);
        setImagePreview(null);
        loadTodayAttendance();
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert(error.message || error.response?.data?.detail || 'Có lỗi xảy ra khi chấm công ra');
    } finally {
      setLoading(false);
    }
  };

  // Kiểm tra điều kiện chấm công
  const canCheckin = !todayAttendance || !todayAttendance.checkin_time;
  // Cho phép checkout luôn (chỉ cần có location và image)
  const canCheckout = location && image && (!todayAttendance || !todayAttendance.checkout_time);
  

  return (
    <div className="attendance-page">
      <div className="attendance-card">
        <h2>Chấm Công Hôm Nay</h2>
        
        {todayAttendance && (
          <div className="attendance-info">
            {todayAttendance.checkin_time && (
              <div className="info-item">
                <span className="label">Giờ vào:</span>
                <span className="value">
                  {new Date(todayAttendance.checkin_time).toLocaleTimeString('vi-VN')}
                </span>
              </div>
            )}
            {todayAttendance.checkout_time && (
              <div className="info-item">
                <span className="label">Giờ ra:</span>
                <span className="value">
                  {new Date(todayAttendance.checkout_time).toLocaleTimeString('vi-VN')}
                </span>
              </div>
            )}
            {todayAttendance.work_hours && (
              <div className="info-item">
                <span className="label">Tổng giờ:</span>
                <span className="value">{todayAttendance.work_hours.toFixed(2)} giờ</span>
              </div>
            )}
          </div>
        )}

        <div className="location-section">
          <h3>📍 Vị trí hiện tại</h3>
          
          {location ? (
            <div className="location-info">
              <p className="location-address">{location.address}</p>
              <p className="location-coords">
                {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
              </p>
            </div>
          ) : locationPermission === 'denied' ? (
            <div className="location-error-box">
              {locationError && locationError.includes('HTTPS') ? (
                <>
                  <p className="location-error-text">
                    ⚠️ {locationError}
                  </p>
                  <p className="location-error-hint">
                    <strong>Giải pháp:</strong>
                  </p>
                  <div className="location-instructions">
                    <div className="instruction-step">
                      <strong>1. Nếu đang test local:</strong> Sử dụng <code>http://localhost:3010</code> hoặc <code>http://127.0.0.1:3010</code>
                    </div>
                    <div className="instruction-step">
                      <strong>2. Nếu đang deploy:</strong> Đảm bảo domain có HTTPS (SSL certificate)
                    </div>
                    <div className="instruction-step">
                      <strong>3. Cloudflare Pages:</strong> HTTPS được bật tự động, kiểm tra URL có <code>https://</code>
                    </div>
                    <div className="instruction-step">
                      <strong>4. Kiểm tra URL hiện tại:</strong> <code>{window.location.href}</code>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <p className="location-error-text">
                    ⚠️ Bạn đã từ chối quyền truy cập vị trí.
                  </p>
                  <p className="location-error-hint">
                    Để chấm công, bạn cần cấp quyền vị trí. Vui lòng làm theo các bước sau:
                  </p>
                  <div className="location-instructions">
                    <div className="instruction-step">
                      <strong>Bước 1:</strong> Nhấn nút "📍 Xin quyền vị trí" bên dưới
                    </div>
                    <div className="instruction-step">
                      <strong>Bước 2:</strong> Nếu không thấy popup, vui lòng vào <strong>Cài đặt</strong> của thiết bị:
                    </div>
                    <div className="instruction-step-sub">
                      <strong>iOS (Safari):</strong><br/>
                      Cài đặt → Safari → Quyền truy cập vị trí → Cho phép
                    </div>
                    <div className="instruction-step-sub">
                      <strong>Android (Chrome):</strong><br/>
                      Cài đặt → Ứng dụng → Chrome → Quyền → Vị trí → Cho phép
                    </div>
                    <div className="instruction-step-sub">
                      <strong>Hoặc:</strong> Cài đặt trình duyệt → Quyền → Vị trí → Cho phép
                    </div>
                    <div className="instruction-step">
                      <strong>Bước 3:</strong> Quay lại trang này và nhấn "Xin quyền vị trí" lại
                    </div>
                  </div>
                  <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRequestLocation();
                }}
                onTouchEnd={(e) => {
                  // CRITICAL: On mobile, touch events don't trigger click automatically
                  // Call handler directly from touch event
                  e.preventDefault();
                  e.stopPropagation();
                  handleRequestLocation();
                }}
                className="btn-request-location"
                disabled={isRequestingLocation}
                style={{ 
                  minHeight: '44px',
                  fontSize: '16px',
                  width: '100%',
                  padding: '12px',
                  cursor: isRequestingLocation ? 'wait' : 'pointer',
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  position: 'relative',
                  zIndex: 10,
                  pointerEvents: 'auto',
                  marginTop: '15px'
                }}
              >
                {isRequestingLocation ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span>Đang yêu cầu...</span>
                  </>
                ) : (
                  <>
                    <Icon name="location" size={18} />
                    <span>Xin quyền vị trí</span>
                  </>
                )}
              </button>
                </>
              )}
            </div>
          ) : locationError ? (
            <div className="location-error-box">
              <p className="location-error-text">{locationError}</p>
              <button 
                onClick={(e) => {
                  // #region agent log
                  const logDataError = {location:'AttendancePage.js:724',message:'button onClick triggered (error)',data:{isRequestingLocation,locationPermission,locationError,userAgent:navigator.userAgent},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'};
                  console.log('[DEBUG] Button click (error):', logDataError);
                  debugLog(logDataError);
                  // #endregion
                  e.preventDefault();
                  e.stopPropagation();
                  requestLocationPermission();
                }}
                onTouchStart={(e) => {
                  // #region agent log
                  const logDataTouchError = {location:'AttendancePage.js:732',message:'button onTouchStart triggered (error)',data:{isRequestingLocation,locationPermission,userAgent:navigator.userAgent},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'};
                  console.log('[DEBUG] Button touch (error):', logDataTouchError);
                  debugLog(logDataTouchError);
                  // #endregion
                }}
                className="btn-request-location"
                disabled={isRequestingLocation}
                style={{ 
                  minHeight: '44px',
                  fontSize: '16px',
                  width: '100%',
                  padding: '12px',
                  cursor: isRequestingLocation ? 'wait' : 'pointer',
                  touchAction: 'manipulation' // Prevent double-tap zoom on mobile
                }}
              >
                {isRequestingLocation ? '⏳ Đang yêu cầu...' : '🔄 Thử lại'}
              </button>
            </div>
          ) : locationPermission === 'prompt' ? (
            <div className="location-prompt-box">
              <p className="location-prompt-text">
                📍 Ứng dụng cần quyền truy cập vị trí để ghi lại nơi chấm công.
                {/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) && (
                  <span style={{ display: 'block', marginTop: '8px', fontSize: '0.9em', color: '#666' }}>
                    💡 Trên thiết bị di động, vui lòng cho phép truy cập vị trí khi trình duyệt yêu cầu.
                  </span>
                )}
              </p>
              <button 
                onClick={(e) => {
                  console.log('[DEBUG] Button onClick (prompt) - START', {isRequestingLocation, locationPermission, userAgent: navigator.userAgent});
                  e.preventDefault();
                  e.stopPropagation();
                  
                  if (!navigator.geolocation) {
                    console.log('[DEBUG] Geolocation not available');
                    setLocationError('Trình duyệt không hỗ trợ định vị');
                    setLocationPermission('denied');
                    return;
                  }
                  
                  console.log('[DEBUG] Setting state and calling getCurrentPosition');
                  setIsUserRequesting(true);
                  setIsRequestingLocation(true);
                  setLocationError(null);
                  setLocationPermission('prompt');
                  
                  // CALL DIRECTLY - no function wrapper, no delay
                  // CRITICAL for mobile browsers (especially iOS Safari)
                  navigator.geolocation.getCurrentPosition(
                    async (position) => {
                      // #region agent log
                      const logDataSuccess = {location:'AttendancePage.js:800',message:'getCurrentPosition success (direct)',data:{latitude:position.coords.latitude,longitude:position.coords.longitude},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'};
                      console.log('[DEBUG]', logDataSuccess);
                      debugLog(logDataSuccess);
                      // #endregion
                      const { latitude, longitude } = position.coords;
                      setLocationPermission('granted');
                      setIsRequestingLocation(false);
                      setIsUserRequesting(false);
                      
                      // TỐI ƯU: Set location ngay lập tức, reverse geocode async không block
                        setLocation({ 
                          latitude, 
                          longitude, 
                          address: '',
                          accuracy: position.coords.accuracy || null
                        });
                      // Reverse geocode trong background (không block UI)
                      reverseGeocode(latitude, longitude).then(address => {
                        setLocation(prev => prev ? { ...prev, address } : null);
                      }).catch(() => {
                        // Ignore geocoding errors, coordinates đã đủ
                      });
                    },
                    (error) => {
                      // #region agent log
                      const logDataError = {location:'AttendancePage.js:825',message:'getCurrentPosition error (direct)',data:{code:error.code,message:error.message},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B,C'};
                      console.log('[DEBUG]', logDataError);
                      debugLog(logDataError);
                      // #endregion
                      setIsRequestingLocation(false);
                      setIsUserRequesting(false);
                      let errorMessage = 'Không thể lấy vị trí';
                      
                      switch(error.code) {
                        case error.PERMISSION_DENIED:
                          errorMessage = 'Bạn đã từ chối quyền truy cập vị trí. Vui lòng cấp quyền trong cài đặt trình duyệt hoặc cài đặt của thiết bị.';
                          setLocationPermission('denied');
                          break;
                        case error.POSITION_UNAVAILABLE:
                          errorMessage = 'Không thể xác định vị trí. Vui lòng kiểm tra GPS và kết nối mạng, sau đó thử lại.';
                          break;
                        case error.TIMEOUT:
                          errorMessage = 'Hết thời gian chờ lấy vị trí. Vui lòng đảm bảo GPS đã bật và thử lại.';
                          break;
                        default:
                          errorMessage = 'Lỗi khi lấy vị trí. Vui lòng thử lại.';
                          break;
                      }
                      
                      setLocationError(errorMessage);
                    },
                    {
                      enableHighAccuracy: true,
                      timeout: 20000,
                      maximumAge: 0
                    }
                  );
                }}
                onTouchStart={(e) => {
                  // #region agent log
                  const logDataTouch = {location:'AttendancePage.js:655',message:'button onTouchStart triggered (prompt)',data:{isRequestingLocation,locationPermission,userAgent:navigator.userAgent},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'};
                  console.log('[DEBUG] Button touch:', logDataTouch);
                  debugLog(logDataTouch);
                  // #endregion
                  // Không preventDefault để onClick vẫn fire
                }} 
                className="btn-request-location"
                disabled={isRequestingLocation}
                style={{ 
                  minHeight: '44px', // Tối thiểu 44px cho mobile touch targets
                  fontSize: '16px', // Tối thiểu 16px để tránh zoom trên iOS
                  width: '100%',
                  padding: '12px',
                  cursor: isRequestingLocation ? 'wait' : 'pointer'
                }}
              >
                {isRequestingLocation ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span>Đang yêu cầu...</span>
                  </>
                ) : (
                  <>
                    <Icon name="location" size={18} />
                    <span>Xin quyền vị trí</span>
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="location-loading">
              {isRequestingLocation ? 'Đang lấy vị trí...' : 'Đang kiểm tra quyền...'}
            </div>
          )}
          
          {location && (
            <button onClick={getCurrentLocation} className="btn-refresh-location">
              🔄 Làm mới vị trí
            </button>
          )}
        </div>

        <div className="image-section">
          <h3>
            <Icon name="camera" size={20} className="icon-primary" />
            <span>Ảnh chấm công</span>
          </h3>

          <div className="beauty-controls-container">
            <div className="beauty-toggle-container">
              <label className="beauty-toggle-label">
                <span className="beauty-toggle-text">Chế độ làm đẹp ✨</span>
                <div className="beauty-switch">
                  <input 
                    type="checkbox" 
                    checked={applyBeauty}
                    onChange={(e) => setApplyBeauty(e.target.checked)}
                  />
                  <span className="beauty-slider"></span>
                </div>
              </label>
            </div>
            
            {applyBeauty && (
              <div className="beauty-intensity-container">
                <label className="beauty-intensity-label">
                  <span className="beauty-intensity-text">Cường độ: {beautyIntensity}%</span>
                </label>
                <div className="beauty-slider-wrapper">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={beautyIntensity}
                    onChange={(e) => setBeautyIntensity(parseInt(e.target.value))}
                    className="beauty-intensity-slider"
                  />
                  <div className="beauty-slider-labels">
                    <span>Nhẹ</span>
                    <span>Vừa</span>
                    <span>Mạnh</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="image-input-options">
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleImageChange}
              className="image-input"
              id="attendance-image"
            />
            <label htmlFor="attendance-image" className="image-input-label">
              {imagePreview ? (
                <img src={imagePreview} alt="Preview" className="image-preview" />
              ) : (
                <div className="image-placeholder">
                  <span>Chọn ảnh</span>
                </div>
              )}
            </label>
            <button
              type="button"
              onClick={() => setShowCameraPreview(true)}
              className="btn-camera-preview"
              title="Mở camera với làm đẹp real-time"
            >
              📷 Camera
            </button>
          </div>

          {image && (
            <button
              onClick={() => {
                setImage(null);
                setImagePreview(null);
              }}
              className="btn-remove-image"
            >
              Xóa ảnh
            </button>
          )}
        </div>

        {/* Hiển thị cảnh báo và mục giải trình khi backend yêu cầu hoặc client-side validation phát hiện */}
        {requiresExplanation && (
          <>
            {validationWarnings.length > 0 && (
            <div className="explanation-warning" role="alert">
              <h3>
                <Icon name="warning" size={20} className="icon-warning" />
                <span>Cần Giải Trình</span>
              </h3>
              <ul className="warning-list">
                {validationWarnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            </div>
            )}
            <div className="explanation-section">
              <h3>
                <Icon name="info" size={20} className="icon-info" />
                <span>Giải Trình <span className="required">*</span></span>
              </h3>
              <textarea
                value={explanation}
                onChange={(e) => setExplanation(e.target.value)}
                placeholder="Bắt buộc phải nhập giải trình: Ví dụ: Đi công tác, Làm việc tại nhà, Xe hỏng..."
                className="explanation-input"
                rows="3"
                required={true}
              />
              {!explanation.trim() && (
                <p className="explanation-hint">
                  <Icon name="warning" size={16} className="icon-warning" />
                  <span>Bắt buộc phải nhập giải trình</span>
                </p>
              )}
            </div>
          </>
        )}

        <div className="action-buttons">
          <button
            onClick={handleCheckin}
            disabled={loading || !canCheckin}
            className={`btn-checkin ${!canCheckin ? 'disabled' : ''}`}
            aria-label="Chấm công vào"
            aria-describedby="checkin-description"
          >
            {loading ? (
              <>
                <LoadingSpinner size="sm" />
                <span>Đang xử lý...</span>
              </>
            ) : (
              <>
                <Icon name="checkCircle" size={20} />
                <span>Chấm Công Vào</span>
              </>
            )}
          </button>
          <p id="checkin-description" className="sr-only">
            Nhấn để chấm công vào. Yêu cầu có vị trí và ảnh.
          </p>
          <button
            onClick={handleCheckout}
            disabled={loading || !canCheckout}
            className={`btn-checkout ${!canCheckout ? 'disabled' : ''}`}
            aria-label="Chấm công ra"
            aria-describedby="checkout-description"
          >
            {loading ? (
              <>
                <LoadingSpinner size="sm" />
                <span>Đang xử lý...</span>
              </>
            ) : (
              <>
                <Icon name="logout" size={20} />
                <span>Chấm Công Ra</span>
              </>
            )}
          </button>
          <p id="checkout-description" className="sr-only">
            Nhấn để chấm công ra. Yêu cầu có vị trí và ảnh, và đã chấm công vào.
          </p>
        </div>
        

        {todayAttendance?.images && todayAttendance.images.length > 0 && (
          <div className="attendance-images">
            <h3>Ảnh đã chụp</h3>
            <div className="images-grid">
              {todayAttendance.images.map((img) => (
                <div key={img.id} className="image-item">
                  <img
                    src={`${apiBaseUrl}${img.path}`}
                    alt={img.type}
                    className="attendance-image"
                  />
                  <span className="image-type">
                    {img.type === 'checkin' ? 'Vào' : 'Ra'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Camera Preview với làm đẹp real-time */}
      {showCameraPreview && (
        <CameraPreview
          onCapture={handleCameraCapture}
          onClose={() => setShowCameraPreview(false)}
          initialBeautyIntensity={beautyIntensity}
          applyBeauty={applyBeauty}
        />
      )}
    </div>
  );
}

export default AttendancePage;

